import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, TrendingDown, TrendingUp, Wallet, AlertTriangle, Building2, Receipt } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";
import { API, fmt, fmtDec, fmtDate, EXPENSE_CATEGORIES, VendorPaymentModal, ExpenseModal, Select } from "./ledgerShared";

const MODE_COLORS = { NEFT: "bg-blue-100 text-blue-700", RTGS: "bg-indigo-100 text-indigo-700", Cheque: "bg-amber-100 text-amber-700", Cash: "bg-emerald-100 text-emerald-700", UPI: "bg-purple-100 text-purple-700", Other: "bg-slate-100 text-slate-600" };

function useProjects() {
  const [projects, setProjects] = useState([]);
  const [all, setAll] = useState([]);
  useEffect(() => { axios.get(`${API}/projects`, { params: { include_archived: true } }).then(r => { setAll(r.data); setProjects(r.data.filter(p => !p.is_archived)); }).catch(() => {}); }, []);
  // Selectors only offer active projects; names resolve for archived ones too ("(archived)" suffix)
  const nameOf = useMemo(() => Object.fromEntries(all.map(p => [p.id, p.is_archived ? `${p.name} (archived)` : p.name])), [all]);
  return { projects, nameOf };
}

function Card({ label, value, tone = "slate", icon: Icon, hint }) {
  const tones = { slate: "text-[#0A1128] bg-[#F8FAFC]", green: "text-emerald-700 bg-emerald-50", red: "text-red-700 bg-red-50", blue: "text-blue-700 bg-blue-50", amber: "text-amber-700 bg-amber-50" };
  const [txt, bg] = tones[tone].split(" ");
  return (
    <div className={`border border-[#E2E8F0] rounded-lg p-4 ${bg}`} title={hint} data-testid={`card-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <div className={`flex items-center gap-1.5 mb-1 ${txt}`}>{Icon && <Icon size={13} />}<span className="text-[10px] font-bold uppercase tracking-wider">{label}</span></div>
      <div className={`font-heading font-black text-xl ${txt}`}>{fmt(value)}</div>
      {hint && <div className="text-[10px] text-[#8AA496] mt-0.5">{hint}</div>}
    </div>
  );
}

// ─── Vendor payments ─────────────────────────────────────────────────────────
export function VendorPaymentsTab() {
  const { user } = useAuth();
  const { projects, nameOf } = useProjects();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | entry
  const [projectFilter, setProjectFilter] = useState("all");
  const canEdit = can(user, "vendor_payments.edit");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/ledger/vendor-payments`); setRows(data); }
    catch { toast.error("Failed to load vendor payments"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this vendor payment? The PO balance will be recalculated.")) return;
    try { await axios.delete(`${API}/ledger/vendor-payments/${id}`); setRows(r => r.filter(x => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const shown = rows.filter(r => projectFilter === "all" || r.project_id === projectFilter);
  const total = shown.reduce((s, r) => s + (r.amount || 0), 0);
  const byVendor = useMemo(() => {
    const m = {}; shown.forEach(r => { m[r.vendor_name] = (m[r.vendor_name] || 0) + r.amount; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [shown]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} testid="vp-filter-project">
            <option value="all">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <span className="text-xs text-[#64748B]">{shown.length} payment{shown.length === 1 ? "" : "s"} · {fmt(total)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#64748B]" title="Refresh"><RefreshCw size={13} /></button>
          {canEdit && <button onClick={() => setModal({})} data-testid="add-vendor-payment-btn" className="flex items-center gap-2 bg-[#0A1128] text-white font-bold px-4 py-2 rounded text-sm hover:bg-[#1B2B4A]"><Plus size={14} /> Pay Vendor</button>}
        </div>
      </div>
      {byVendor.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {byVendor.map(([v, amt]) => <span key={v} className="text-[11px] px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#1B2B22]"><Building2 size={10} className="inline mr-1" />{v}: <b>{fmt(amt)}</b></span>)}
        </div>
      )}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="bg-[#F5F8F5] text-[#1B2B22] text-xs">
            <th className="px-4 py-3 text-left font-semibold">Date</th><th className="px-4 py-3 text-left font-semibold">Project</th><th className="px-4 py-3 text-left font-semibold">Vendor</th>
            <th className="px-4 py-3 text-left font-semibold">PO</th><th className="px-4 py-3 text-right font-semibold">Amount</th><th className="px-4 py-3 text-left font-semibold">Mode</th>
            <th className="px-4 py-3 text-left font-semibold">Reference</th>{canEdit && <th className="px-4 py-3 text-center font-semibold w-20"></th>}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-[#94A3B8]">Loading…</td></tr>
              : shown.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-[#94A3B8]">No vendor payments yet. Record what you pay to suppliers and contractors here — against a PO where one exists.</td></tr>
              : shown.map((r, i) => (
                <tr key={r.id} className={`border-b border-[#F1F5F9] ${i % 2 ? "bg-[#F8FAFC]" : ""}`} data-testid={`vp-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-[#64748B] whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3 text-xs text-[#0A1128] font-medium">{nameOf[r.project_id] || r.project_id}</td>
                  <td className="px-4 py-3 text-xs text-[#0A1128]">{r.vendor_name}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B] font-mono">{r.po_number || <span className="text-[#CBD5E1]">ad-hoc</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-red-700 whitespace-nowrap">{fmtDec(r.amount)}{r.tds_amount ? <div className="text-[10px] font-normal text-[#94A3B8]">TDS {fmt(r.tds_amount)}</div> : null}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${MODE_COLORS[r.mode] || MODE_COLORS.Other}`}>{r.mode}</span></td>
                  <td className="px-4 py-3 text-xs text-[#64748B] font-mono">{r.reference || <span className="text-[#CBD5E1]">—</span>}</td>
                  {canEdit && <td className="px-4 py-3 text-center"><div className="flex justify-center gap-1">
                    <button onClick={() => setModal(r)} className="p-1.5 rounded hover:bg-blue-50 text-[#94A3B8] hover:text-blue-600" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
                  </div></td>}
                </tr>))}
          </tbody>
        </table>
      </div>
      {modal && <VendorPaymentModal projects={projects} initial={modal.id ? modal : null} onClose={() => setModal(null)}
        onSaved={(e, isEdit) => setRows(prev => isEdit ? prev.map(x => x.id === e.id ? e : x) : [e, ...prev])} />}
    </div>
  );
}

// ─── Expenses ────────────────────────────────────────────────────────────────
export function ExpensesTab() {
  const { user } = useAuth();
  const { projects, nameOf } = useProjects();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [projectFilter, setProjectFilter] = useState("all");
  const canEdit = can(user, "expenses.edit");
  const catLabel = Object.fromEntries(EXPENSE_CATEGORIES);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/ledger/expenses`); setRows(data); }
    catch { toast.error("Failed to load expenses"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await axios.delete(`${API}/ledger/expenses/${id}`); setRows(r => r.filter(x => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const shown = rows.filter(r => projectFilter === "all" || r.project_id === projectFilter);
  const total = shown.reduce((s, r) => s + (r.amount || 0), 0);
  const byCat = useMemo(() => {
    const m = {}; shown.forEach(r => { m[r.category] = (m[r.category] || 0) + r.amount; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [shown]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} testid="exp-filter-project">
            <option value="all">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <span className="text-xs text-[#64748B]">{shown.length} expense{shown.length === 1 ? "" : "s"} · {fmt(total)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#64748B]" title="Refresh"><RefreshCw size={13} /></button>
          {canEdit && <button onClick={() => setModal({})} data-testid="add-expense-btn" className="flex items-center gap-2 bg-[#0A1128] text-white font-bold px-4 py-2 rounded text-sm hover:bg-[#1B2B4A]"><Plus size={14} /> Record Expense</button>}
        </div>
      </div>
      {byCat.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {byCat.map(([c, amt]) => <span key={c} className="text-[11px] px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#1B2B22]">{catLabel[c] || c}: <b>{fmt(amt)}</b></span>)}
        </div>
      )}
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="bg-[#F5F8F5] text-[#1B2B22] text-xs">
            <th className="px-4 py-3 text-left font-semibold">Date</th><th className="px-4 py-3 text-left font-semibold">Project</th><th className="px-4 py-3 text-left font-semibold">Category</th>
            <th className="px-4 py-3 text-left font-semibold">Description</th><th className="px-4 py-3 text-left font-semibold">Paid to</th><th className="px-4 py-3 text-right font-semibold">Amount</th>
            <th className="px-4 py-3 text-left font-semibold">Mode</th>{canEdit && <th className="px-4 py-3 text-center font-semibold w-20"></th>}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-[#94A3B8]">Loading…</td></tr>
              : shown.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-[#94A3B8]">No expenses yet. Labour, transport, civil work, liaison — anything you pay without a PO goes here so the cash position is complete.</td></tr>
              : shown.map((r, i) => (
                <tr key={r.id} className={`border-b border-[#F1F5F9] ${i % 2 ? "bg-[#F8FAFC]" : ""}`} data-testid={`exp-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-[#64748B] whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3 text-xs text-[#0A1128] font-medium">{nameOf[r.project_id] || r.project_id}</td>
                  <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#1B2B22] text-[10px] font-bold uppercase">{catLabel[r.category] || r.category}</span></td>
                  <td className="px-4 py-3 text-xs text-[#64748B] max-w-56 truncate" title={r.description}>{r.description || <span className="text-[#CBD5E1]">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{r.payee || <span className="text-[#CBD5E1]">—</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-red-700 whitespace-nowrap">{fmtDec(r.amount)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${MODE_COLORS[r.mode] || MODE_COLORS.Other}`}>{r.mode}</span></td>
                  {canEdit && <td className="px-4 py-3 text-center"><div className="flex justify-center gap-1">
                    <button onClick={() => setModal(r)} className="p-1.5 rounded hover:bg-blue-50 text-[#94A3B8] hover:text-blue-600" title="Edit"><Pencil size={13} /></button>
                    <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
                  </div></td>}
                </tr>))}
          </tbody>
        </table>
      </div>
      {modal && <ExpenseModal projects={projects} initial={modal.id ? modal : null} onClose={() => setModal(null)}
        onSaved={(e, isEdit) => setRows(prev => isEdit ? prev.map(x => x.id === e.id ? e : x) : [e, ...prev])} />}
    </div>
  );
}

// ─── Cash position ───────────────────────────────────────────────────────────
export function CashPositionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/ledger/cash-position`, { params: { include_archived: includeArchived } }); setData(data); }
    catch { toast.error("Failed to load cash position"); }
    setLoading(false);
  }, [includeArchived]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-xs text-[#94A3B8] py-8 text-center">Loading…</div>;
  const t = data?.totals || {};
  const rows = data?.projects || [];
  const netTone = t.net_position >= 0 ? "green" : "red";

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card label="Received from clients" value={t.received} tone="green" icon={TrendingUp} hint={`of ${fmt(t.billed)} billed · ${fmt(t.contract_value)} contracted`} />
        <Card label="Paid to vendors" value={t.vendor_paid} tone="red" icon={TrendingDown} hint={`${fmt(t.vendor_outstanding)} still due on POs`} />
        <Card label="Other expenses" value={t.expenses} tone="amber" icon={Receipt} hint="labour, transport, civil, liaison…" />
        <Card label={t.net_position >= 0 ? "Client money in hand" : "Funded by you (EPC)"} value={Math.abs(t.net_position)} tone={netTone} icon={t.net_position >= 0 ? Wallet : AlertTriangle}
          hint={t.net_position >= 0 ? "received exceeds what has gone out" : "you have put in more than you have received"} />
      </div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[#64748B]">
          Across {t.projects} project{t.projects === 1 ? "" : "s"}: <b className="text-red-700">{fmt(t.funded_by_epc)}</b> funded by you on some, <b className="text-emerald-700">{fmt(t.excess_from_clients)}</b> held from clients on others · projected margin <b>{fmt(t.projected_margin)}</b>{t.projected_margin_pct != null ? ` (${t.projected_margin_pct}%)` : ""}
        </div>
        <label className="text-xs text-[#64748B] flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} /> include archived</label>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead><tr className="bg-[#F5F8F5] text-[#1B2B22] text-[11px]">
            <th className="px-3 py-3 text-left font-semibold">Project</th>
            <th className="px-3 py-3 text-right font-semibold">Contract</th>
            <th className="px-3 py-3 text-right font-semibold">Billed</th>
            <th className="px-3 py-3 text-right font-semibold text-emerald-700">Received</th>
            <th className="px-3 py-3 text-right font-semibold">PO committed</th>
            <th className="px-3 py-3 text-right font-semibold text-red-700">Paid to vendors</th>
            <th className="px-3 py-3 text-right font-semibold text-amber-700">Expenses</th>
            <th className="px-3 py-3 text-right font-semibold">Net position</th>
            <th className="px-3 py-3 text-right font-semibold">Proj. margin</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-[#94A3B8]">No projects.</td></tr>
              : rows.map((r, i) => (
                <tr key={r.project_id} className={`border-b border-[#F1F5F9] ${i % 2 ? "bg-[#F8FAFC]" : ""}`} data-testid={`cash-row-${r.project_id}`}>
                  <td className="px-3 py-3 text-xs">
                    <a href={`/app/projects/${r.project_id}`} className="font-semibold text-[#0A1128] hover:text-[#2F7D52]">{r.project_name}</a>
                    <div className="text-[10px] text-[#8AA496]">{r.client_name}{r.is_archived ? " · archived" : ""}{r.received_pct != null ? ` · ${r.received_pct}% received` : ""}</div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs">{fmt(r.contract_value)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs">{fmt(r.billed)}<div className="text-[10px] text-[#8AA496]">{r.receivable > 0 ? `${fmt(r.receivable)} due` : "settled"}</div></td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs font-semibold text-emerald-700">{fmt(r.received)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs">{fmt(r.po_committed)}<div className="text-[10px] text-[#8AA496]">{r.vendor_outstanding > 0 ? `${fmt(r.vendor_outstanding)} unpaid` : "paid"}</div></td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs font-semibold text-red-700">{fmt(r.vendor_paid)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-xs text-amber-700">{fmt(r.expenses)}</td>
                  <td className={`px-3 py-3 text-right tabular-nums text-xs font-bold ${r.net_position >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {r.net_position >= 0 ? "+" : "−"}{fmt(Math.abs(r.net_position))}
                    <div className="text-[10px] font-normal text-[#8AA496]">{r.net_position >= 0 ? "client-funded" : "EPC-funded"}</div>
                  </td>
                  <td className={`px-3 py-3 text-right tabular-nums text-xs ${r.projected_margin >= 0 ? "text-[#0A1128]" : "text-red-700"}`}>{fmt(r.projected_margin)}{r.projected_margin_pct != null ? <div className="text-[10px] text-[#8AA496]">{r.projected_margin_pct}%</div> : null}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-[#8AA496] mt-3">Net position = received from client − (paid to vendors + expenses). Projected margin = contract − PO committed − expenses. Voided POs/PIs are excluded.</p>
    </div>
  );
}
